// El cuestionario de onboarding es obligatorio hasta terminarlo — también al NAVEGAR.
// Ver ADR-2026-09-23-el-cuestionario-es-obligatorio-tambien-al-navegar.
//
// El rebote de `App` (`checkUserProfile`) corre sólo en eventos de sesión: al arrancar
// y en `SIGNED_IN`. Una navegación SPA no pasa por ahí. O sea que a quien pagó por el
// alta por link y tiene la empresa con el rubro centinela, cualquier salida de la cadena
// post-pago que cayera en la app —el "Volver" del portal de facturación tras refrescar
// el detalle de una factura, por ejemplo— lo dejaba adentro sin cuestionario hasta la
// próxima recarga. La obligación valía al entrar, no al moverse.
//
// Dos piezas, las dos sin estado en la base:
//   · un flag a nivel de módulo, que `checkUserProfile` prende cuando LEE el centinela
//     (lectura positiva, igual que el rebote: nunca por un error) y que el cuestionario
//     apaga cuando escribe el rubro. Sobrevive los remontes del árbol de rutas porque no
//     es un ref; muere con la pestaña, que es lo correcto: la próxima carga vuelve a leer.
//   · un guardia montado junto al router que, en cada cambio de ruta, manda al
//     cuestionario si el flag está prendido y la ruta no está exenta.
//
// La lista de rutas exentas es UNA sola y la usan los dos (el rebote y el guardia):
// tenerla dos veces es exactamente cómo se desincronizan.

import { ONBOARDING_V2 } from '../components/onboarding/onboardingV2Routes';

/**
 * Rutas donde NO se interrumpe a quien tiene el cuestionario pendiente. Prefijos.
 *
 *  · el cuestionario mismo (si no, loop),
 *  · el alta por link y todas las pantallas donde se paga o se acaba de pagar:
 *    interrumpir a alguien camino a pagar, o entre el pago y la oferta de factura, es
 *    peor que esperar a que salga de ahí,
 *  · los flujos que crean la empresa por su cuenta (Tiendanube) o que no tienen que
 *    crearla (invitación a un equipo),
 *  · las pantallas de auth: sin sesión no hay cuestionario que deber.
 */
export const RUTAS_EXENTAS_DEL_CUESTIONARIO: readonly string[] = [
  ONBOARDING_V2.setup, // '/configuracion-cuenta' y sus pasos
  '/alta-empresa',
  '/capacitacion-ultra',
  '/facturacion',
  '/checkout',
  '/subscription',
  '/verificar-celular',
  '/tiendanube',
  '/unirme',
  '/auth',
  '/registro',
  '/restablecer-clave',
];

/** ¿En esta ruta se deja en paz a quien tiene el cuestionario pendiente? Pura. */
export function rutaExentaDelCuestionario(ruta: string | null | undefined): boolean {
  const r = ruta ?? '';
  return RUTAS_EXENTAS_DEL_CUESTIONARIO.some((prefijo) => r.startsWith(prefijo));
}

let pendiente = false;

/**
 * Lo llama `checkUserProfile` cuando leyó la fila de la empresa y su rubro es el
 * centinela. SÓLO con lectura positiva: un error de red no puede prender esto, por la
 * misma razón por la que el rebote falla cerrado.
 */
export function marcarCuestionarioPendiente(): void {
  pendiente = true;
}

/**
 * Lo llama el cuestionario apenas escribe el rubro real, y `App` al cerrar sesión. Si
 * el cuestionario no lo apagara, la pantalla de resultados —que navega a la app— sería
 * rebotada de vuelta al paso 1: el loop que hay que evitar.
 */
export function cuestionarioCompletado(): void {
  pendiente = false;
}

/** ¿Hay que mandar a esta ruta al cuestionario? Pura sobre el flag + la ruta. */
export function debeIrAlCuestionario(ruta: string | null | undefined): boolean {
  return pendiente && !rutaExentaDelCuestionario(ruta);
}
